import { useEffect, useMemo, useRef, useState } from "react";
import styles from "./Date.module.scss";
import { ClickAwayListener, Popper } from "@mui/material";
import { Controller } from "react-hook-form";
import type {
  Control,
  FieldValues,
  Path,
  RegisterOptions,
  UseFormRegister,
} from "react-hook-form";
import withLabelAndError from "../withLabelAndError";
import { combineClasses } from "@/lib/utils";

export interface DateInputProps<T extends FieldValues = FieldValues> {
  control?: Control<T>;
  register?: UseFormRegister<T>;
  validations?: RegisterOptions<T, Path<T>>;
  name: Path<T>;
  placeholder?: string;
  min?: string;
  max?: string;
  className?: string;
  [key: string]: unknown;
}

const WEEK_DAYS = ["S", "M", "T", "W", "T", "F", "S"];
const YEARS_PER_PAGE = 12;
const YEAR_GRID_ACTIVE_OFFSET = 2;
const MONTH_NAME_FORMATTER = new Intl.DateTimeFormat("en-US", {
  month: "long",
});

const padValue = (value: number) => String(value).padStart(2, "0");

const formatDateValue = (date: Date) =>
  `${date.getFullYear()}-${padValue(date.getMonth() + 1)}-${padValue(date.getDate())}`;

const parseDateValue = (value?: string) => {
  if (!value) return null;

  const [year, month, day] = value.split("-").map(Number);

  if (!year || !month || !day) return null;

  const parsedDate = new Date(year, month - 1, day);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const startOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1);

const clampValue = (value: number, minValue: number, maxValue: number) =>
  Math.min(Math.max(value, minValue), maxValue);

const getInitialDisplayDate = (
  selectedDate: Date | null,
  minDate: Date | null,
  maxDate: Date | null,
  fallbackDate: Date,
) => {
  if (selectedDate) return selectedDate;

  const fallbackDateKey = formatDateValue(fallbackDate);

  if (minDate && fallbackDateKey < formatDateValue(minDate)) return minDate;
  if (maxDate && fallbackDateKey > formatDateValue(maxDate)) return maxDate;

  return minDate ?? maxDate ?? fallbackDate;
};

const getDisplayMonthForYear = (
  year: number,
  month: number,
  minDate: Date | null,
  maxDate: Date | null,
) => {
  let nextMonth = month;

  if (
    minDate &&
    maxDate &&
    year === minDate.getFullYear() &&
    year === maxDate.getFullYear()
  ) {
    nextMonth = Math.min(Math.max(nextMonth, minDate.getMonth()), maxDate.getMonth());
  } else {
    if (minDate && year === minDate.getFullYear()) {
      nextMonth = Math.max(nextMonth, minDate.getMonth());
    }

    if (maxDate && year === maxDate.getFullYear()) {
      nextMonth = Math.min(nextMonth, maxDate.getMonth());
    }
  }

  return new Date(year, nextMonth, 1);
};

const getYearPageStart = (year: number, minYear: number, maxYear: number) => {
  const maxPageStart = Math.max(minYear, maxYear - YEARS_PER_PAGE + 1);

  return clampValue(year - YEAR_GRID_ACTIVE_OFFSET, minYear, maxPageStart);
};

const formatDisplayDate = (value?: string, placeholder = "dd / mm / yyyy") => {
  const parsedDate = parseDateValue(value);

  if (!parsedDate) return placeholder;

  return `${padValue(parsedDate.getDate())}/${padValue(parsedDate.getMonth() + 1)}/${parsedDate.getFullYear()}`;
};

const isDateDisabled = (date: Date, min?: string, max?: string) => {
  const dateKey = formatDateValue(date);

  if (min && dateKey < min) return true;
  if (max && dateKey > max) return true;

  return false;
};

const canNavigateToPreviousMonth = (displayMonth: Date, min?: string) => {
  if (!min) return true;

  const previousMonthLastDay = new Date(
    displayMonth.getFullYear(),
    displayMonth.getMonth(),
    0,
  );

  return formatDateValue(previousMonthLastDay) >= min;
};

const canNavigateToNextMonth = (displayMonth: Date, max?: string) => {
  if (!max) return true;

  const nextMonthFirstDay = new Date(
    displayMonth.getFullYear(),
    displayMonth.getMonth() + 1,
    1,
  );

  return formatDateValue(nextMonthFirstDay) <= max;
};

const CalendarIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M8 2V5M16 2V5M3 9H21M5 4H19C20.1046 4 21 4.89543 21 6V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V6C3 4.89543 3.89543 4 5 4Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronLeftIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M15 18L9 12L15 6"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronRightIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M9 18L15 12L9 6"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

interface ControlledDateFieldProps {
  value?: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  name: string;
  placeholder?: string | undefined;
  min?: string | undefined;
  max?: string | undefined;
  disabled?: boolean | undefined;
  className?: string | undefined;
}

const ControlledDateField = ({
  value,
  onChange,
  onBlur,
  name,
  placeholder,
  min,
  max,
  disabled,
  className,
}: ControlledDateFieldProps) => {
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const selectedDate = useMemo(() => parseDateValue(value), [value]);
  const minDate = useMemo(() => parseDateValue(min), [min]);
  const maxDate = useMemo(() => parseDateValue(max), [max]);
  const today = useMemo(() => new Date(), []);
  const baselineYear = selectedDate?.getFullYear() ?? today.getFullYear();
  const minSelectableYear = Math.min(
    minDate?.getFullYear() ?? baselineYear,
    baselineYear,
    1900,
  );
  const maxSelectableYear = Math.max(
    maxDate?.getFullYear() ?? baselineYear,
    baselineYear,
    2100,
  );
  const initialDisplayDate = useMemo(
    () => getInitialDisplayDate(selectedDate, minDate, maxDate, today),
    [maxDate, minDate, selectedDate, today],
  );
  const [isOpen, setIsOpen] = useState(false);
  const [isYearPickerOpen, setIsYearPickerOpen] = useState(false);
  const [displayMonth, setDisplayMonth] = useState<Date>(startOfMonth(initialDisplayDate));
  const [yearPageStart, setYearPageStart] = useState(() =>
    getYearPageStart(initialDisplayDate.getFullYear(), minSelectableYear, maxSelectableYear),
  );

  useEffect(() => {
    if (selectedDate) {
      const nextDisplayMonth = startOfMonth(selectedDate);
      setDisplayMonth(nextDisplayMonth);
      setYearPageStart(
        getYearPageStart(nextDisplayMonth.getFullYear(), minSelectableYear, maxSelectableYear),
      );
    }
  }, [maxSelectableYear, minSelectableYear, selectedDate]);

  const calendarDays = useMemo(() => {
    const firstDayOfMonth = startOfMonth(displayMonth);
    const gridStart = new Date(firstDayOfMonth);
    gridStart.setDate(firstDayOfMonth.getDate() - firstDayOfMonth.getDay());

    return Array.from({ length: 42 }, (_, index) => {
      const currentDate = new Date(gridStart);
      currentDate.setDate(gridStart.getDate() + index);

      return {
        date: currentDate,
        isCurrentMonth: currentDate.getMonth() === displayMonth.getMonth(),
        isDisabled: isDateDisabled(currentDate, min, max),
      };
    });
  }, [displayMonth, max, min]);

  const closePicker = () => {
    setIsOpen(false);
    setIsYearPickerOpen(false);
    onBlur?.();
  };

  const openPicker = () => {
    if (disabled) return;

    const nextDisplayMonth = startOfMonth(initialDisplayDate);
    setDisplayMonth(nextDisplayMonth);
    setYearPageStart(
      getYearPageStart(nextDisplayMonth.getFullYear(), minSelectableYear, maxSelectableYear),
    );
    setIsYearPickerOpen(false);
    setIsOpen(true);
  };

  const togglePicker = () => {
    if (isOpen) {
      closePicker();
      return;
    }

    openPicker();
  };

  const handleDateSelect = (date: Date) => {
    if (isDateDisabled(date, min, max)) return;

    onChange(formatDateValue(date));
    setDisplayMonth(startOfMonth(date));
    setYearPageStart(getYearPageStart(date.getFullYear(), minSelectableYear, maxSelectableYear));
    closePicker();
  };

  const handleTodayClick = () => {
    if (isDateDisabled(today, min, max)) return;
    handleDateSelect(today);
  };

  const handleClearClick = () => {
    onChange("");
    closePicker();
  };

  const handleYearSelect = (nextYear: number) => {
    setDisplayMonth((currentMonth) =>
      getDisplayMonthForYear(nextYear, currentMonth.getMonth(), minDate, maxDate),
    );
    setYearPageStart(getYearPageStart(nextYear, minSelectableYear, maxSelectableYear));
    setIsYearPickerOpen(false);
  };

  const canGoToPreviousMonth = canNavigateToPreviousMonth(displayMonth, min);
  const canGoToNextMonth = canNavigateToNextMonth(displayMonth, max);
  const maxYearPageStart = Math.max(minSelectableYear, maxSelectableYear - YEARS_PER_PAGE + 1);
  const yearOptions = useMemo(() => {
    return Array.from({ length: YEARS_PER_PAGE }, (_, index) => yearPageStart + index).filter(
      (year) => year <= maxSelectableYear,
    );
  }, [maxSelectableYear, yearPageStart]);
  const yearRangeLabel = useMemo(() => {
    const rangeEnd = Math.min(yearPageStart + YEARS_PER_PAGE - 1, maxSelectableYear);

    return `${yearPageStart} - ${rangeEnd}`;
  }, [maxSelectableYear, yearPageStart]);
  const canGoToPreviousYearPage = yearPageStart > minSelectableYear;
  const canGoToNextYearPage = yearPageStart < maxYearPageStart;
  const handleYearPickerToggle = () => {
    if (isYearPickerOpen) {
      setIsYearPickerOpen(false);
      return;
    }

    setYearPageStart(
      getYearPageStart(displayMonth.getFullYear(), minSelectableYear, maxSelectableYear),
    );
    setIsYearPickerOpen(true);
  };

  return (
    <div className={styles.pickerWrapper}>
      <button
        ref={triggerRef}
        id={name}
        type="button"
        className={combineClasses(
          styles.inputField,
          isOpen && styles.isOpen,
          !value && styles.placeholderState,
          className,
        )}
        onClick={togglePicker}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        disabled={disabled}
      >
        <span className={combineClasses(styles.valueText, !value && styles.placeholderText)}>
          {formatDisplayDate(value, placeholder || "dd / mm / yyyy")}
        </span>
        <span className={styles.icon}>
          <CalendarIcon />
        </span>
      </button>

      <Popper
        open={isOpen}
        anchorEl={triggerRef.current}
        placement="bottom-start"
        className={styles.popper}
        modifiers={[
          {
            name: "flip",
            enabled: true,
            options: {
              fallbackPlacements: ["top-start", "bottom-end", "top-end"],
              padding: 8,
            },
          },
          {
            name: "preventOverflow",
            enabled: true,
            options: {
              altAxis: true,
              tether: true,
              padding: 8,
            },
          },
        ]}
      >
        <ClickAwayListener onClickAway={closePicker}>
          <div className={styles.popup} role="dialog" aria-label="Choose date">
            <div className={styles.header}>
              <button
                type="button"
                className={styles.navButton}
                onClick={() =>
                  canGoToPreviousMonth &&
                  setDisplayMonth(
                    new Date(displayMonth.getFullYear(), displayMonth.getMonth() - 1, 1),
                  )
                }
                disabled={!canGoToPreviousMonth}
                aria-label="Previous month"
              >
                <ChevronLeftIcon />
              </button>

              <div className={styles.headerTitle}>
                <button
                  type="button"
                  className={combineClasses(
                    styles.headerValueButton,
                    isYearPickerOpen && styles.headerValueButtonActive,
                  )}
                  onClick={handleYearPickerToggle}
                >
                  {displayMonth.getFullYear()}
                </button>
                <div className={styles.monthLabel}>
                  {MONTH_NAME_FORMATTER.format(displayMonth)}
                </div>
              </div>

              <button
                type="button"
                className={styles.navButton}
                onClick={() =>
                  canGoToNextMonth &&
                  setDisplayMonth(
                    new Date(displayMonth.getFullYear(), displayMonth.getMonth() + 1, 1),
                  )
                }
                disabled={!canGoToNextMonth}
                aria-label="Next month"
              >
                <ChevronRightIcon />
              </button>
            </div>

            <div className={styles.bodyDivider} />

            {isYearPickerOpen ? (
              <div className={styles.yearPickerPanel}>
                <div className={styles.yearPager}>
                  <button
                    type="button"
                    className={styles.yearPagerButton}
                    onClick={() =>
                      canGoToPreviousYearPage &&
                      setYearPageStart((currentStart) =>
                        Math.max(minSelectableYear, currentStart - YEARS_PER_PAGE),
                      )
                    }
                    disabled={!canGoToPreviousYearPage}
                    aria-label="Previous years"
                  >
                    <ChevronLeftIcon />
                  </button>
                  <div className={styles.yearRangeLabel}>{yearRangeLabel}</div>
                  <button
                    type="button"
                    className={styles.yearPagerButton}
                    onClick={() =>
                      canGoToNextYearPage &&
                      setYearPageStart((currentStart) =>
                        Math.min(maxYearPageStart, currentStart + YEARS_PER_PAGE),
                      )
                    }
                    disabled={!canGoToNextYearPage}
                    aria-label="Next years"
                  >
                    <ChevronRightIcon />
                  </button>
                </div>

                <div className={styles.yearGrid}>
                  {yearOptions.map((year) => {
                    const isSelectedYear = displayMonth.getFullYear() === year;

                    return (
                      <button
                        key={year}
                        type="button"
                        className={combineClasses(
                          styles.yearButton,
                          isSelectedYear && styles.selectedYearButton,
                        )}
                        onClick={() => handleYearSelect(year)}
                        aria-pressed={isSelectedYear}
                      >
                        {year}
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              <>
                <div className={styles.weekdays}>
                  {WEEK_DAYS.map((day, index) => (
                    <span key={`${day}-${index}`} className={styles.weekday}>
                      {day}
                    </span>
                  ))}
                </div>

                <div className={styles.daysGrid}>
                  {calendarDays.map(({ date, isCurrentMonth, isDisabled: disabledDay }) => {
                    const isSelected = value === formatDateValue(date);
                    const isToday = formatDateValue(date) === formatDateValue(today);

                    return (
                      <button
                        key={`${date.toISOString()}-${isCurrentMonth}`}
                        type="button"
                        className={combineClasses(
                          styles.dayButton,
                          !isCurrentMonth && styles.outsideMonth,
                          isToday && styles.todayButton,
                          isSelected && styles.selectedDay,
                          disabledDay && styles.disabledDay,
                        )}
                        onClick={() => handleDateSelect(date)}
                        disabled={disabledDay}
                        aria-pressed={isSelected}
                      >
                        {date.getDate()}
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            <div
              className={combineClasses(
                styles.footer,
                isYearPickerOpen && styles.footerSingleAction,
              )}
            >
              <button type="button" className={styles.footerButton} onClick={handleClearClick}>
                Clear
              </button>
              {!isYearPickerOpen && (
                <button
                  type="button"
                  className={combineClasses(styles.footerButton, styles.footerButtonPrimary)}
                  onClick={handleTodayClick}
                  disabled={isDateDisabled(today, min, max)}
                >
                  Today
                </button>
              )}
            </div>
          </div>
        </ClickAwayListener>
      </Popper>
    </div>
  );
};

const DateInput = <T extends FieldValues = FieldValues>({
  control,
  register,
  validations,
  name,
  placeholder,
  min,
  max,
  className,
  ...rest
}: DateInputProps<T>) => {
  const renderNativeInput = (field: Record<string, unknown> = {}) => (
    <input
      {...field}
      {...rest}
      id={name}
      type="date"
      min={min}
      max={max}
      className={combineClasses(styles.nativeInput, className)}
      placeholder={placeholder}
    />
  );

  if (control) {
    return (
      <Controller
        control={control}
        name={name}
        {...(validations ? { rules: validations } : {})}
        render={({ field }) => (
          <ControlledDateField
            {...rest}
            name={name}
            placeholder={placeholder}
            min={min}
            max={max}
            className={className}
            value={(field.value as string) || ""}
            onChange={field.onChange}
            onBlur={field.onBlur}
          />
        )}
      />
    );
  }

  if (register) {
    const field = register(name, validations);
    return renderNativeInput(field);
  }

  return renderNativeInput();
};

const DateInputWithLabelAndError = withLabelAndError(DateInput);

export default DateInputWithLabelAndError;
export { DateInput };
